<template>
  <iSelect
    v-model="value"
    :items="selectItems"
    value-field="value"
    text-field="label"
    return-value
    :label="labelValue"
    :width="getOptionValue(LayoutWidth)"
    :empty-string="placeholderValue"
    :style-overrides="inputStyleOverrides"
  />
</template>

<script>
import { plainLabelText } from "%/dynamic-component-editor/helpers/plain-label-text.mjs";
import App from "@bloglovin/inspire-engine/App";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import DynamicElementWithContext from "%/dynamic-component-editor/mixins/dynamic-element-with-context-mixin";
import DynamicAppInputOptionsMixin from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/dynamic-app-input-options-mixin";
import CommitAction from "%/dynamic-component-editor/options/commit-action";
import SeedField from "%/dynamic-component-editor/options/seed-field";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import Placeholder from "%/dynamic-component-editor/options/placeholder";
import Label from "%/dynamic-component-editor/options/label";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import BasicTextOptions from "%/dynamic-component-editor/bundles/basic-text-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BorderColor from "%/dynamic-component-editor/options/border-color";
import BorderWidth from "%/dynamic-component-editor/options/border-width";

function enumOptionLabel(option) {
  const words = String(option)
      .replace(/[_-]+/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .trim()
      .toLowerCase()
      .split(/\s+/);
  if (words.length === 0 || words[0] === "") {
    return "";
  }
  const [first, ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ");
}

export default {
  name: "DynamicAppSelectInputElement",
  mixins: [DynamicElementMixin, DynamicElementWithContext, DynamicAppInputOptionsMixin],
  elementPanelSettings: {
    displayName: "Select",
    elementGroup: "general",
    displayIcon: "list",
    elementType: "element",
  },
  hidden: true,
  options: [
    CommitAction,
    SeedField,
    Placeholder,
    Label,
    BackgroundColor,
    BorderColor,
    BorderWidth,
  ],
  optionBundles: [
    new BasicTextOptions({}, {
      "font-family": true,
    }),
    new LayoutWidthHeightOptions({}, {
      "layout-height": true,
      "max-width": true,
      "max-height": true,
      "min-width": true,
      "min-height": true,
    }),
    new SpacingOptions(),
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
  ],
  computed: {
    LayoutWidth() {
      return LayoutWidth;
    },
    placeholderValue() {
      if (!this.appInputOptionIsSet(this.getOptionKey(Placeholder))) {
        return undefined;
      }
      return this.appInputResolvedValue(this.getOptionValue(Placeholder)) ?? undefined;
    },
    labelValue() {
      return plainLabelText(this.getOptionValue(Label));
    },
    configurationProblems() {
      if (this.itemsField) {
        return "";
      }
      if (this.editMode && this.elementContext?.field && !Array.isArray(this.boundFieldDefinition?.enumOptions)) {
        return `"${this.elementContext.field}" is not an enum field, so it offers no choices to select from.`;
      }
      return "";
    },
    itemsField() {
      return this.elementContext?.itemsField || "";
    },
    itemsFeedOwnerId() {
      return this.contextItem?.entityPkUrlVars?.id ?? 0;
    },
    itemsFromFeed() {
      const entityPk = this.contextItem?.entityPkUrlVars;
      if (!this.itemsField || !this.entityType || !entityPk?.id) {
        return [];
      }
      let app = null;
      try {
        app = App.get(this.resolvedAppName);
      } catch {
        return [];
      }
      const rows = app.getFeedItems(this.entityType, entityPk, this.itemsField) ?? [];
      const valueField = this.elementContext?.itemValueField || "";
      const labelField = this.elementContext?.itemLabelField || valueField;
      return rows
          .map(row => row?.entityPk ?? row?.pk ?? null)
          .filter(Boolean)
          .map(pk => {
            const read = field => {
              try {
                return app.getEntityField(this.itemsEntityType, pk, field);
              } catch {
                return null;
              }
            };
            return { value: read(valueField), label: read(labelField) };
          })
          .filter(item => item.value !== null && item.value !== undefined && item.value !== "");
    },
    itemsEntityType() {
      return this.relatedEntity?.fields?.[this.itemsField]?.entity ?? "";
    },
    selectItems() {
      if (this.itemsField) {
        return this.itemsFromFeed;
      }
      const options = this.boundFieldDefinition?.enumOptions;
      if (!Array.isArray(options)) {
        return [];
      }
      return options.map(option => ({ value: option, label: enumOptionLabel(option) }));
    },
    inputStyleOverrides() {
      const padding = this.appInputPadding;
      return this.appInputStyleOverrides({
        inputBorderRadius: this.appInputBorderRadius,
        dropdownBorderRadius: this.appInputBorderRadius,
        inputBackgroundColor: this.appInputBackgroundColor,
        inputHoverBackgroundColor: this.appInputBackgroundColor,
        inputBorderColor: this.appInputBorderColor,
        inputBorderWidth: this.appInputBorderWidth,
        inputFontColor: this.appInputFontColor,
        inputFontWeight: this.appInputFontWeight,
        standardInputFontSize: this.appInputFontSize,
        standardInputPaddingTop: padding.top ?? null,
        standardInputPaddingBottom: padding.bottom ?? null,
        standardInputPaddingLeft: padding.left ?? null,
        standardInputPaddingRight: padding.right ?? null,
      });
    },
    value: {
      get() {
        const draft = this.valueFromContext;
        if (draft === null || draft === undefined || draft === "") {
          return this.appInputSeededValue;
        }
        return draft;
      },
      set(value) {
        if (this.editMode) {
          return;
        }
        this.valueFromContext = value;
        this.commitOnChange();
      },
    },
  },
  watch: {
    itemsFeedOwnerId: {
      immediate: true,
      handler() {
        this.refreshItemsFeed();
      },
    },
  },
  methods: {
    refreshItemsFeed() {
      if (this.editMode || !this.itemsField || !this.entityType || !this.itemsFeedOwnerId) {
        return;
      }
      let app = null;
      try {
        app = App.get(this.resolvedAppName);
      } catch {
        return;
      }
      app.getFreshFeedItems(this.entityType, this.contextItem.entityPkUrlVars, this.itemsField);
    },
    async commitOnChange() {
      const commitAction = this.getOptionValue(CommitAction);
      if (!commitAction) {
        return;
      }
      await this.$nextTick();
      await this.triggerNamedAction(commitAction);
      this.refreshItemsFeed();
    },
  },
};
</script>
